import React, {useState, useEffect} from 'react';
import {Input, Button, Modal, ModalBody, ModalFooter, ModalHeader, ListGroup, ListGroupItem} from 'reactstrap';
import SearchBar from '../../components/SearchBar';

import './CountrySelect.scss';

const CountrySelect = ({
                           value = [],
                           countries,
                           onChange,
                           placeholder,
                           modal,
                           toggle,
                           multiple = true,
                           hideInput = false,
                           showWorldwide = true
}) => {
    const [inputValue, setInputValue] = useState(value);
    const [filteredValues, setFilteredValues] = useState(countries);
    const [searchValue, setSearchInput] = useState('');

    useEffect(() => {
        setInputValue(prev => {
            if (Array.isArray(value) && Array.isArray(prev)) {
                if (value.length === prev.length && value.every((v, i) => v === prev[i])) {
                    return prev;
                }
            }
            return value;
        });
    }, [value]);

    useEffect(() => {
        if (searchValue) {
            setFilteredValues(countries.filter(({ name }) =>
                name.toLowerCase().includes(searchValue.toLowerCase())
            ));
        } else {
            setFilteredValues(countries);
        }
    }, [countries, searchValue]);

    const computeDisplayValue = () => {
        if (multiple) {
            if (inputValue.includes('worldwide')) {
                return 'Worldwide';
            }

            let displayValue = inputValue.map(val => countries.find(({code}) => code === val)?.name)
                .slice(0,3)
                .join(', ');

            if (inputValue.length === countries.length) {
                return 'Worldwide';
            }

            if (inputValue.length > 3) {
                displayValue += ` and ${inputValue.length - 3} more`;
            }

            return displayValue;
        } else {
            return countries.find(({code}) => code === inputValue)?.name || '';
        }
    }

    const handleSubmit = () => {
        onChange(inputValue);
        setSearchInput('');
        setFilteredValues(countries);
        toggle();
    };

    const handleToggle = () => {
        setSearchInput('');
        setFilteredValues(countries);
        toggle();
    }

    const handleChange = country => event => {
        event.preventDefault();
        if (multiple) {
            if (inputValue.includes('worldwide')) {
                setInputValue([country]);
                return;
            }

            const countryIndex = inputValue.findIndex(item => item === country);

            if (countryIndex !== -1) {
                const updatedInputValue = inputValue.filter((item, index) => index !== countryIndex);
                setInputValue(updatedInputValue);
            } else {
                setInputValue([...inputValue, country]);
            }
        } else {
            setInputValue(country);
            onChange(country);
            setSearchInput('');
            toggle();
        }
    };

    const handleFilter = event => {
        event.preventDefault();
        setSearchInput(event.target.value);
        setFilteredValues(countries.filter(({ name }) => name.toLowerCase().includes(event.target.value.toLowerCase())));
    };

    const selectAllCountries = () => {
        setInputValue(['worldwide']);
    };

    const clearAllCountries = () => {
        setInputValue([]);
    };

    return (
        <div>
            <div className={`${hideInput ? 'd-none' : 'd-flex'} align-items-center`}>
                <Input
                    type="text"
                    value={computeDisplayValue()}
                    readOnly
                    placeholder={placeholder}
                    onClick={toggle}
                />
            </div>

            <Modal isOpen={modal} toggle={handleToggle} scrollable size="lg" className="country-select-modal" autoFocus={false}>
                <ModalHeader toggle={handleToggle}>
                    {multiple ? 'Select countries' : 'Select a country'}
                </ModalHeader>
                <ModalBody className="px-0 position-relative">
                    <div className="px-3 mb-3">
                        {/* Focused on open, so typing filters the list straight away. */}
                        <SearchBar
                            autoFocus
                            onChange={handleFilter}
                            initialValue={searchValue}
                            disabled={inputValue.includes('worldwide')}
                        />
                    </div>
                    <ListGroup className="">
                        {filteredValues.map(({code, name}) => {
                            const isSelected = !inputValue.includes('worldwide') && inputValue.includes(code);
                            return (
                                <ListGroupItem
                                    onClick={handleChange(code)}
                                    key={code}
                                    active={isSelected}
                                    color={isSelected ? 'primary' : 'secondary'}
                                    style={{ cursor: 'pointer' }}
                                    className="position-relative"
                                >
                                    {name}
                                    {isSelected && (
                                        <span className="fas fa-check-circle text-primary position-absolute" />
                                    )}
                                </ListGroupItem>
                            );
                        })}
                    </ListGroup>
                    {inputValue.includes('worldwide') && (
                        <div
                            className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center text-center"
                            style={{
                                backgroundColor: 'rgba(0, 0, 0, 0.85)',
                                zIndex: 1000,
                                padding: '2rem',
                                pointerEvents: 'all'
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <span className="fas fa-globe fa-3x text-primary mb-3" />
                            <h5 className="text-white mb-2">Worldwide Selected</h5>
                            <p className="text-white-50 mb-0">
                                Click any country below to select specific regions instead
                            </p>
                        </div>
                    )}
                </ModalBody>
                {multiple && (
                    <ModalFooter className="d-flex justify-content-between">
                        <div>
                            {showWorldwide && (
                                <Button
                                    color={inputValue.includes('worldwide') ? 'primary' : 'link'}
                                    onClick={selectAllCountries}
                                    className="text-decoration-none"
                                >
                                    <span className="fas fa-globe me-2" />
                                    {inputValue.includes('worldwide') && <span className="fas fa-check-circle me-2" />}
                                    Worldwide
                                </Button>
                            )}
                            <Button color="link" onClick={clearAllCountries} className="text-decoration-none">
                                <span className="fas fa-times-circle me-2" /> Clear All
                            </Button>
                        </div>
                        <Button color="primary" onClick={handleSubmit}>
                            <span className="fas fa-save me-2" /> Select {inputValue.includes('worldwide') ? '(Worldwide)' : inputValue.length ? `(${inputValue.length})` : ''}
                        </Button>
                    </ModalFooter>
                )}
            </Modal>
        </div>
    );
};

export default CountrySelect;
